import React, { useEffect, useState } from "react";
import { PRODUCTS, getOfferings, purchasePackage } from "../lib/iap";
import { useSubscription } from "../context/SubscriptionContext";
import { getSessionId } from "../lib/session";
import { api } from "../lib/api";
import { Check, Sparkles, ShieldCheck, RefreshCw, Loader2, Ticket } from "lucide-react";

const features = [
  "AI-adaptive workouts that learn from your body",
  "1000+ exercise library with injury-safe filters",
  "AI meal plans + Food Snap macro vision",
  "Personal AI Coach chat, 24/7",
  "Progressive overload + routine builder",
  "Recovery & physio sessions",
];

export default function Paywall({ onClose }) {
  const { refresh, restore } = useSubscription();
  const [eb, setEb] = useState(null); // {available, spots_left}
  const annualKey = eb?.available ? "annual_earlybird" : "annual";
  const annualMeta = eb?.available ? PRODUCTS.annual_earlybird : PRODUCTS.annual;
  const [selected, setSelected] = useState("annual");
  const [products, setProducts] = useState([
    { key: "monthly",     ...PRODUCTS.monthly     },
    { key: "three_month", ...PRODUCTS.three_month },
    { key: "six_month",   ...PRODUCTS.six_month   },
    { key: "annual",      ...PRODUCTS.annual      },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Inline redeem state — lets the user apply a friend code OR the owner
  // bypass code BEFORE paying. Critical: the Settings redeem panel is gated
  // behind Pro, so without this the bypass becomes a chicken-and-egg.
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState("");

  const handleRedeem = async () => {
    const code = (redeemCode || "").trim().toUpperCase();
    if (!code || code.length < 4) {
      setRedeemMsg("Enter a valid code");
      return;
    }
    setRedeemBusy(true);
    setRedeemMsg("");
    try {
      const res = await api.redeemReferralCode(code);
      setRedeemMsg(res?.message || "Activated.");
      // Subscription state will flip to Pro on the next refresh — wait a beat
      // for the DB write to settle, then refresh and close the paywall.
      setTimeout(async () => {
        try { await refresh(); } catch { /* ignore */ }
        onClose && onClose();
      }, 600);
    } catch (e) {
      setRedeemMsg(e?.response?.data?.detail || e?.message || "Could not redeem");
    } finally {
      setRedeemBusy(false);
    }
  };

  useEffect(() => {
    let alive = true;
    api.earlybirdStatus()
      .then((s) => { if (alive) setEb(s); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    (async () => {
      const offers = await getOfferings();
      setProducts([
        { key: "monthly",     ...(offers.find(o => o.id === PRODUCTS.monthly.id)     || PRODUCTS.monthly)     },
        { key: "three_month", ...(offers.find(o => o.id === PRODUCTS.three_month.id) || PRODUCTS.three_month) },
        { key: "six_month",   ...(offers.find(o => o.id === PRODUCTS.six_month.id)   || PRODUCTS.six_month)   },
        { key: "annual",      ...(offers.find(o => o.id === annualMeta.id)           || annualMeta)           },
      ]);
    })();
  }, [annualMeta]);

  const handlePurchase = async () => {
    setBusy(true); setError("");
    const purchaseKey = selected === "annual" ? annualKey : selected;
    const res = await purchasePackage(purchaseKey, getSessionId());
    if (res.redirecting) return; // Web: browser is navigating to Stripe Checkout
    setBusy(false);
    if (res.ok) {
      await refresh();
      onClose && onClose();
    } else if (!res.cancelled) {
      setError(res.error || "Purchase failed. Please try again.");
    }
  };

  const handleRestore = async () => {
    setBusy(true);
    const ok = await restore();
    setBusy(false);
    if (ok) onClose && onClose();
    else setError("No active subscription to restore.");
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0b0b0c] text-white overflow-y-auto"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="paywall-screen"
    >
      <div className="mx-auto max-w-md px-6 pt-10 pb-32">
        <div className="flex justify-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-lime-400 to-emerald-600 grid place-items-center shadow-lg">
            <Sparkles className="h-7 w-7 text-black" />
          </div>
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-center leading-tight">
          Unlock <span className="text-lime-400">FreeFitFuel Pro</span>
        </h1>
        <p className="text-center text-zinc-400 mt-2 text-sm">
          Your full coach, library, and AI tools — built for results.
        </p>

        <ul className="mt-7 space-y-2.5">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-200">
              <Check className="h-4 w-4 mt-0.5 text-lime-400 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="mt-7 space-y-3">
          {products.map((p) => {
            const active = selected === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setSelected(p.key)}
                data-testid={`paywall-${p.key}-option`}
                className={`w-full text-left rounded-2xl border p-4 transition-all ${
                  active
                    ? "border-lime-400 bg-lime-400/10 ring-1 ring-lime-400"
                    : "border-zinc-800 bg-zinc-900/60 hover:border-zinc-700"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">
                      {p.key === "annual" ? "Annual" : p.key === "six_month" ? "6 months" : p.key === "three_month" ? "3 months" : "Monthly"}
                      {p.key === "annual" && eb?.available && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#BEFF38] text-black px-2 py-0.5 rounded-full" data-testid="paywall-founder-badge">
                          Founder · {eb.spots_left} left
                        </span>
                      )}
                      {p.key === "annual" && !eb?.available && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider bg-lime-400 text-black px-2 py-0.5 rounded-full">
                          Best value
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">
                      {p.key === "annual" ? annualMeta.price : p.price} / {p.period}
                      {p.key === "annual" && eb?.available && (
                        <span className="ml-2 text-[#7c8597] line-through">£18</span>
                      )}
                      {p.key === "annual" && <span className="ml-2 text-lime-400">{annualMeta.savings}</span>}
                      {p.key === "six_month" && <span className="ml-2 text-lime-400">{PRODUCTS.six_month.savings}</span>}
                      {p.key === "three_month" && <span className="ml-2 text-lime-400">{PRODUCTS.three_month.savings}</span>}
                    </div>
                  </div>
                  <div className={`h-5 w-5 rounded-full border-2 ${active ? "border-lime-400 bg-lime-400" : "border-zinc-600"}`}>
                    {active && <Check className="h-4 w-4 text-black" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {error && <div className="mt-4 text-sm text-red-400 text-center" data-testid="paywall-error">{error}</div>}

        <button
          onClick={handlePurchase}
          disabled={busy}
          data-testid="paywall-subscribe-btn"
          className="mt-5 w-full rounded-full bg-lime-400 text-black font-semibold py-3.5 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy ? "Processing…" : `Start ${selected === "annual" ? "annual" : selected === "six_month" ? "6-month" : selected === "three_month" ? "3-month" : "monthly"} plan`}
        </button>

        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-zinc-500">
          <button onClick={handleRestore} disabled={busy} data-testid="paywall-restore-btn" className="flex items-center gap-1 hover:text-zinc-300">
            <RefreshCw className="h-3 w-3" /> Restore purchases
          </button>
          <span className="opacity-50">·</span>
          <button
            onClick={() => setRedeemOpen((v) => !v)}
            data-testid="paywall-have-code-btn"
            className="flex items-center gap-1 hover:text-zinc-300"
          >
            <Ticket className="h-3 w-3" /> Have a code?
          </button>
          <span className="opacity-50">·</span>
          <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Cancel anytime</span>
        </div>

        {redeemOpen && (
          <div data-testid="paywall-redeem-row" className="mt-3 flex items-center gap-2">
            <input
              type="text"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              placeholder="Paste your code"
              data-testid="paywall-redeem-input"
              className="flex-1 bg-zinc-900 border border-zinc-800 rounded-full px-4 h-10 text-sm text-white placeholder-zinc-500 focus:border-lime-400 focus:outline-none uppercase tracking-wider"
              maxLength={32}
              autoFocus
            />
            <button
              onClick={handleRedeem}
              disabled={redeemBusy}
              data-testid="paywall-redeem-submit"
              className="h-10 px-4 rounded-full bg-zinc-100 text-black text-[11px] font-black uppercase tracking-wider disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {redeemBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Apply
            </button>
          </div>
        )}
        {redeemOpen && redeemMsg && (
          <p data-testid="paywall-redeem-msg" className="mt-2 text-[11px] text-center text-zinc-400">{redeemMsg}</p>
        )}

        <p className="mt-5 text-[11px] leading-snug text-zinc-500 text-center">
          Payment will be charged to your Apple ID at confirmation. Subscription auto-renews unless cancelled at least
          24 hours before the end of the current period. Manage in Settings &gt; Apple ID &gt; Subscriptions.
        </p>

        <div className="mt-3 flex items-center justify-center gap-4 text-[11px] text-zinc-500">
          <a href="/terms" className="hover:text-zinc-300" data-testid="paywall-terms-link">Terms</a>
          <span className="opacity-50">·</span>
          <a href="/privacy" className="hover:text-zinc-300" data-testid="paywall-privacy-link">Privacy</a>
        </div>
      </div>
    </div>
  );
}
